/**
 * Reine Berechnungsfunktionen für Gebäude.
 * Produktion, Energie, Kapazität, Upgrade-Kosten — alles ohne State.
 */
import {
  UPGRADE_COST_BASE, UPGRADE_COST_ADD, COST_GROWTH,
  UPGRADE_TIME_BASE, UPGRADE_TIME_GROWTH,
  STORAGE_SEG1_TARGET, STORAGE_SEG1_MAX_LEVEL,
  STORAGE_SEG2_START, STORAGE_MAX,
} from '../config/BuildingsConfig';

/** Fabriken: konstant 5% compound pro Level. */
export function factoryProduction(level: number, baseRate: number): number {
  if (level <= 0) return 0;
  return baseRate * Math.pow(1.05, level - 1);
}

/** Energie-Verbrauch: +5%/Level compound. */
export function factoryEnergy(level: number, baseEnergy: number): number {
  if (level <= 0 || baseEnergy === 0) return 0;
  return baseEnergy * Math.pow(1.05, level - 1);
}

/** Solar-Kollektor: konstant 4.7% compound pro Level. */
export function solarOutput(level: number, baseOutput: number): number {
  if (level <= 0) return 0;
  return baseOutput * Math.pow(1.047, level - 1);
}

/** Basis-Lager ohne Gebäude. */
export const BASE_STORAGE = 500;

/** Stückweise: Lv1–74 flach, Lv75 = 1 Mio, Lv75–100 steil. Konstanten aus BuildingsConfig. */
const STORAGE_SEG1_K = (STORAGE_SEG1_TARGET - BASE_STORAGE) / 1000 / Math.pow(STORAGE_SEG1_MAX_LEVEL, 1.5);
const STORAGE_SEG2_SPAN = 100 - (STORAGE_SEG1_MAX_LEVEL + 1);
const STORAGE_SEG2_K = (STORAGE_MAX - STORAGE_SEG2_START) / 1000 / (STORAGE_SEG2_SPAN * STORAGE_SEG2_SPAN);

/** Lager: stückweise — Anstieg in die 1 Mio bei Lv75, danach steil bis 66 Mio. */
export function storageCapacity(level: number, baseCapacity: number): number {
  if (level <= 0) return BASE_STORAGE;
  if (level <= STORAGE_SEG1_MAX_LEVEL) {
    return BASE_STORAGE + baseCapacity * STORAGE_SEG1_K * Math.pow(level, 1.5);
  }
  const n = level - (STORAGE_SEG1_MAX_LEVEL + 1);
  return STORAGE_SEG2_START + baseCapacity * STORAGE_SEG2_K * n * n;
}

export interface UpgradeCost {
  readonly feronium: number;
  readonly chrysalium: number;
  readonly neurogel: number;
}

/** Bauzeit in Sekunden für Upgrade auf targetLevel. 5s × 1.10^(level-1). */
export function upgradeTime(targetLevel: number): number {
  if (targetLevel <= 1) return UPGRADE_TIME_BASE;
  return UPGRADE_TIME_BASE * Math.pow(UPGRADE_TIME_GROWTH, targetLevel - 1);
}

/** Kosten für Upgrade auf targetLevel. Additiv + exponentiell für Progression ab Lv1. */
export function upgradeCost(
  targetLevel: number,
  weights?: { feronium: number; chrysalium: number; neurogel: number },
): UpgradeCost {
  const base = UPGRADE_COST_ADD + UPGRADE_COST_BASE * Math.pow(COST_GROWTH, targetLevel - 1);
  const w = weights ?? { feronium: 1, chrysalium: 1, neurogel: 1 };
  return {
    feronium: Math.ceil(base * w.feronium),
    chrysalium: Math.ceil(base * w.chrysalium),
    neurogel: Math.ceil(base * w.neurogel),
  };
}
